import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Users, Edit, Trash2 } from 'lucide-react';
import api from '../../../api.js';
import IntermediaryForm from '../forms/IntermediaryForm.jsx';

// Only these supplier categories are valid broker/intermediary types
const BROKER_CATEGORIES = ['broker', 'insurer', 'agent'];

const CATEGORY_COLORS = {
  broker: 'bg-blue-100 text-blue-800',
  insurer: 'bg-green-100 text-green-800',
  agent: 'bg-purple-100 text-purple-800',
};

function IntermediariesTab({ onMessage }) {
  const { t } = useTranslation();
  const [intermediaries, setIntermediaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const loadIntermediaries = async () => {
    try {
      setLoading(true);
      // Load from suppliers table, filtered to broker-compatible categories only
      const response = await api.supplier.getAll();
      const allSuppliers = response.data || [];
      const brokers = allSuppliers.filter(s => BROKER_CATEGORIES.includes(s.category));
      setIntermediaries(brokers);
    } catch (error) {
      console.error('Error loading intermediaries:', error);
      onMessage?.({ title: t('insuranceConfig.toast.errorTitle'), message: t('insuranceConfig.intermediaries.loadError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntermediaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t('insuranceConfig.intermediaries.confirmDelete'))) return;
    try {
      await api.supplier.delete(id);
      onMessage?.({ title: t('insuranceConfig.toast.successTitle'), message: t('insuranceConfig.intermediaries.deleted'), type: 'success' });
      loadIntermediaries();
    } catch (error) {
      onMessage?.({ title: t('insuranceConfig.toast.errorTitle'), message: t('insuranceConfig.intermediaries.deleteError'), type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('insuranceConfig.intermediaries.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showForm && (
        <IntermediaryForm
          intermediary={editingItem}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
          onSuccess={() => { loadIntermediaries(); setShowForm(false); setEditingItem(null); }}
          onMessage={onMessage}
        />
      )}

      <div className="flex justify-between items-center">
        <p className="text-gray-600">{t('insuranceConfig.intermediaries.total', { count: intermediaries.length })}</p>
        <button
          onClick={() => { setEditingItem(null); setShowForm(true); }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          {t('insuranceConfig.intermediaries.addButton')}
        </button>
      </div>

      {intermediaries.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('insuranceConfig.intermediaries.emptyTitle')}</h3>
          <p className="text-gray-600">{t('insuranceConfig.intermediaries.emptyDescription')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('insuranceConfig.intermediaries.columnName')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('insuranceConfig.intermediaries.columnType')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('insuranceConfig.intermediaries.columnContact')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('insuranceConfig.intermediaries.columnCity')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('insuranceConfig.intermediaries.columnActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {intermediaries.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    <div className="text-sm text-gray-500">{item.company_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${CATEGORY_COLORS[item.category] || 'bg-gray-100 text-gray-800'}`}>
                      {t(`insuranceConfig.intermediaryTypes.${item.category}`, { defaultValue: item.category })}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{item.contact_name}</div>
                    <div className="text-sm text-gray-500">{item.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.city}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <button
                      onClick={() => { setEditingItem(item); setShowForm(true); }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default IntermediariesTab;
